/**
 * createConcurrencyGate — cap how many async tasks run at once AND, optionally,
 * how often a new one may start (a minimum interval between starts).
 *
 * Used to keep client-side fan-out (e.g. the per-drop SMSWiser stats fetch on the
 * "All drops" view) under both the backend's request-rate throttler and
 * SMSWiser's stricter per-request rate limit, so the tasks queue and all complete
 * instead of mostly 429-ing. Concurrency alone isn't enough when each call is
 * fast (low latency → high rate), so `minIntervalMs` paces the START rate.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

export interface ConcurrencyGate {
  run<T>(task: () => Promise<T>): Promise<T>;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function createConcurrencyGate(
  max: number,
  minIntervalMs = 0,
): ConcurrencyGate {
  const limit = Math.max(1, Math.floor(max));
  const interval = Math.max(0, minIntervalMs);
  let active = 0;
  const waiters: Array<() => void> = [];
  let nextStartAt = 0;

  const acquireSlot = (): Promise<void> => {
    if (active < limit) {
      active += 1;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => waiters.push(resolve)).then(() => {
      active += 1;
    });
  };

  const reserveStartDelay = (): number => {
    if (interval === 0) return 0;
    const now = Date.now();
    const startAt = Math.max(now, nextStartAt);
    nextStartAt = startAt + interval;
    return startAt - now;
  };

  const release = (): void => {
    active -= 1;
    waiters.shift()?.();
  };

  return {
    async run<T>(task: () => Promise<T>): Promise<T> {
      await acquireSlot();
      try {
        const wait = reserveStartDelay();
        if (wait > 0) await sleep(wait);
        return await task();
      } finally {
        release();
      }
    },
  };
}
